import { compact } from "lodash";
import { DownloadAsPdfButtonProps } from "@/common/commonComponents";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import {
  documentApi,
  DocumentForViewingReadyState,
  GetDocumentForViewingResponse,
} from "./documentApi";
import { AppBarData } from "@/appBar/appBarDomain";
import { Language } from "@/i18n/languages";
import { when } from "@/utils/functions";
import { LandingPageHeaderProps } from "@/common/landingPageComponents";
import { prepLandingPageHeader } from "@/common/landingPageDomain";
import { formatOrgNum } from "@/utils/other";

export const initDocumentForViewingPage = (params: {
  documentId: string;
  recipientId?: string;
}): DCEvent[] => [
  { type: "update-store", data: { documentForViewing: { type: "pending" } } },
  documentApi.getDocumentForViewing({
    ...params,
    onSuccess: (response) => [
      { type: "update-store", data: { documentForViewing: { type: "ready", data: response } } },
    ],
    onFailure: (err) => [
      { type: "update-store", data: { documentForViewing: { type: "failure", err } } },
    ],
  }),
];

const downloadEvents = (state: DocumentForViewingReadyState, url: string): DCEvent[] => [
  { type: "update-store", data: { documentForViewing: { ...state, isDownloading: true } } },
  {
    type: "download-file",
    url,
    onSuccess: [
      { type: "update-store", data: { documentForViewing: { ...state, isDownloading: false } } },
    ],
    onFailure: [
      { type: "update-store", data: { documentForViewing: { ...state, isDownloading: false } } },
    ],
  },
];

export const prepDownloadAsPdfButton = (
  state: DocumentForViewingReadyState,
  { variant }: { variant?: DownloadAsPdfButtonProps["variant"] } = {},
): DownloadAsPdfButtonProps => ({
  isDownloading: state.isDownloading,
  onDownload: downloadEvents(state, state.data.downloadPdfUrl),
  onDownloadWithAttachments: when(state.data.downloadWithAttachmentsUrl, (url) =>
    downloadEvents(state, url),
  ),
  variant: variant ?? "secondary",
});

export const openDocumentAttachment = (
  { attachments }: { attachments: UploadedAttachment[] },
  id: string,
): DCEvent[] => {
  const attachment = attachments.find((attachment) => attachment.id === id);
  return compact([
    attachment?.downloadUrl && {
      type: "preview-file",
      filename: attachment.filename,
      file: { url: attachment.downloadUrl },
    },
  ]);
};

export const prepDocumentForViewingAppBar = ({
  response,
  appBar,
  lang,
  dispatch,
}: {
  response: GetDocumentForViewingResponse;
  appBar: AppBarData | null;
  lang: Language;
  dispatch: EventDispatcher;
}): LandingPageHeaderProps => {
  return prepLandingPageHeader({
    appBar,
    dispatch,
    lang,
    companyContext: {
      title: { translated: response.companyName },
      subtitle: { translated: formatOrgNum(response.orgNo) },
    },
    title: { translated: response.title },
    user: {
      name: response.recipientName,
      initials: response.recipientInitials,
      email: response.recipientEmail,
    },
  });
};
